/**
 * Component for selecting filter options.
 * This component has two tabs: "Search results" and "Selected"
 */
import * as React from 'react';

import { Tab, TabContext, Tabs } from '@coursera/cds-core';

import FilterOptionList from 'bundles/enterprise-admin-drawers/components/components/FilterOptionList';
import type { FilterOption, FilterType } from 'bundles/enterprise-admin-drawers/components/types';
import { Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/enterprise-admin-drawers';

import 'css!bundles/enterprise-admin-drawers/components/__styles__/FilterDrawer';

export type TabOption = 'selected' | 'search';

export type Props = {
  filterOptions: FilterOption[];
  filterType: FilterType;
  loading: boolean;
  selectedFilterOptions: FilterOption[];
  onChange: (filterOption: FilterOption, checked: boolean) => void;
  onChangeSelectAll?: (nextValue: FilterOption[]) => void;
  className?: string;
  searchQuery?: string;
  preSorted?: boolean;
  defaultTab?: TabOption;
  optionListHeader?: JSX.Element;
  listLabel?: string;
};

const TabbedFilterOptionList = ({
  filterOptions,
  filterType,
  selectedFilterOptions,
  onChange,
  onChangeSelectAll,
  loading,
  className,
  searchQuery,
  preSorted,
  defaultTab = 'search',
  optionListHeader,
  listLabel,
}: Props) => {
  const [currentTab, setCurrentTab] = React.useState<string>(defaultTab);

  return (
    <div className={`rc-TabbedFilterOptionList vertical-box ${className}`}>
      <Spacer size={24} />
      <TabContext value={currentTab}>
        <Tabs onChange={setCurrentTab}>
          <Tab label={_t('Search result (#{count})', { count: filterOptions.length })} value="search" />
          <Tab label={_t('Selected (#{count})', { count: selectedFilterOptions.length })} value="selected" />
        </Tabs>
      </TabContext>
      <Spacer size={24} />
      <div className="c-tabContent flex-1 mh-0 vertical-box">
        {currentTab === 'search' && (
          <FilterOptionList
            filterOptions={filterOptions}
            filterType={filterType}
            selectedFilterOptions={selectedFilterOptions}
            onChange={onChange}
            onChangeSelectAll={onChangeSelectAll}
            loading={loading}
            emptyText={searchQuery && searchQuery.length > 0 ? _t('No results.') : _t('Type to search.')}
            className="h-100"
            preSorted={preSorted}
            header={optionListHeader}
            listLabel={listLabel}
          />
        )}

        {currentTab === 'selected' && (
          <FilterOptionList
            filterOptions={selectedFilterOptions}
            filterType={filterType}
            selectedFilterOptions={selectedFilterOptions}
            onChange={onChange}
            onChangeSelectAll={onChangeSelectAll}
            emptyText={_t('No filters selected.')}
            className="h-100"
            preSorted={preSorted}
            data-testid="selected-filter-options"
            header={optionListHeader}
            listLabel={listLabel}
          />
        )}
      </div>
    </div>
  );
};

export default TabbedFilterOptionList;
